import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Button, Field, Input } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { adminApi, type AdminChallenge } from '../../lib/adminApi';
import { useAdminAuth } from '../../lib/adminAuth';
import { usePageTitle } from '../../lib/usePageTitle';

/**
 * The only way into the portal.
 *
 * There is deliberately **no "create an account" and no "forgot password"**.
 * Administrators are created by another administrator or by a script on the
 * server, and a forgotten password is recovered by a colleague — a reset link
 * reachable from the internet would put the whole customer base behind one
 * mailbox. The screen says so rather than leaving an operator hunting for a
 * link that was omitted on purpose.
 */
export function AdminLogin() {
  usePageTitle('Admin sign-in — WaveQA');

  const location = useLocation();
  const { admin, loading, signIn } = useAdminAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<AdminChallenge | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && admin) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/admin'} replace />;
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);

    try {
      const result = await adminApi.login(email, password);

      if (result.session) {
        // Only reachable for an administrator whose second factor another
        // administrator has deliberately switched off.
        signIn(result.session);
        return;
      }

      setChallenge(result.challenge);
      setNotice('We emailed a sign-in code to that address.');
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : 'Could not sign in. Try again.',
      );
    } finally {
      setBusy(false);
      // The password is no longer needed; dropping it keeps it out of the form
      // state a later error screen would re-render.
      setPassword('');
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;

    setError(null);
    setBusy(true);

    try {
      signIn(await adminApi.verifyOtp(challenge.challenge_id, code));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That code was not accepted.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!challenge) return;
    setError(null);
    setBusy(true);

    try {
      setChallenge(await adminApi.resendOtp(challenge.challenge_id));
      setNotice('A new code is on its way.');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-hull-950 px-5 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <p className="text-sm font-semibold tracking-[0.22em] text-white">WAVEQA</p>
          <h1 className="mt-2 text-2xl font-semibold text-white">Administrator</h1>
          <p className="mt-2 text-sm text-hull-400">
            This portal is for WaveQA staff. Customers sign in on the main site.
          </p>
        </div>

        <div className="rounded-card border border-hull-700 bg-hull-800/60 p-6">
          {error && <Alert tone="error">{error}</Alert>}
          {notice && !error && <Alert tone="info">{notice}</Alert>}

          {challenge === null ? (
            <form onSubmit={(event) => void submitPassword(event)} noValidate>
              <Field label="Email" htmlFor="admin-email">
                <Input
                  id="admin-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>

              <Field label="Password" htmlFor="admin-password">
                <Input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>

              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? 'Checking…' : 'Continue'}
              </Button>
            </form>
          ) : (
            <form onSubmit={(event) => void submitCode(event)} noValidate>
              <p className="mb-4 text-sm text-hull-300">
                Enter the {challenge.otp_digits}-digit code we emailed you.
              </p>

              <Field label="Sign-in code" htmlFor="admin-code">
                <Input
                  id="admin-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={challenge.otp_digits}
                  required
                  autoFocus
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  className="text-center text-lg tracking-[0.4em]"
                />
              </Field>

              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? 'Verifying…' : 'Sign in'}
              </Button>

              <div className="mt-4 flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => void resend()}
                  disabled={busy}
                  className="text-surge-400 underline underline-offset-2 hover:text-surge-300 disabled:opacity-50"
                >
                  Resend the code
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChallenge(null);
                    setCode('');
                    setNotice(null);
                    setError(null);
                  }}
                  className="text-hull-400 hover:text-hull-200"
                >
                  Start over
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs leading-relaxed text-hull-500">
          Sign-in is restricted to addresses the server allows, and accounts are
          created by another administrator. There is no self-registration and no
          password reset by email — if you are locked out, ask a colleague with
          portal access.
        </p>
      </div>
    </div>
  );
}
